import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { OfflineModeContext } from '../contexts/OfflineModeContext';
import api from '../api';
import { get as idbGet, del as idbDel } from 'idb-keyval';
import { cacheIndividualRoutines } from '../utils/offlineSync';
import DrillCard from '../components/DrillCard';
import EquipmentBadges from '../components/EquipmentBadges';
import StatsBanner from '../components/StatsBanner';
import ChallengeLeaderboard from '../components/ChallengeLeaderboard';
import { Dumbbell, Activity, HeartPulse, Grid, Trophy } from 'lucide-react';

import homeCache from '../services/homeCache';

const getCategoryStyle = (categoryName) => {
    const lower = categoryName.toLowerCase();
    if (lower.includes('active challenge')) return 'bg-gradient-to-br from-yellow-500/20 to-transparent border-l-4 border-l-yellow-500';
    if (lower.includes('past challenge')) return 'bg-gradient-to-br from-slate-500/20 to-transparent border-l-4 border-l-slate-500';
    if (lower.includes('court') || lower.includes('squash')) return 'bg-gradient-to-br from-emerald-500/10 to-transparent border-l-4 border-l-emerald-500';
    if (lower.includes('fitness') || lower.includes('strength')) return 'bg-gradient-to-br from-orange-500/10 to-transparent border-l-4 border-l-orange-500';
    if (lower.includes('recovery') || lower.includes('mobility')) return 'bg-gradient-to-br from-blue-500/10 to-transparent border-l-4 border-l-blue-500';
    return 'bg-gradient-to-br from-slate-500/10 to-transparent border-l-4 border-l-slate-500';
};

const getCategoryWatermark = (categoryName) => {
    const lower = categoryName.toLowerCase();
    const watermarkClasses = "absolute -bottom-4 -right-4 w-32 h-32 text-white/5";
    
    if (lower.includes('challenge')) {
        return <Trophy className={watermarkClasses} />;
    }
    if (lower.includes('court') || lower.includes('squash')) {
        return <Activity className={watermarkClasses} />;
    }
    if (lower.includes('fitness') || lower.includes('strength')) {
        return <Dumbbell className={watermarkClasses} />;
    }
    if (lower.includes('recovery') || lower.includes('mobility')) {
        return <HeartPulse className={watermarkClasses} />;
    }
    return <Grid className={watermarkClasses} />;
};

const RoutineListPage = () => {
    const navigate = useNavigate();
    const [activeRoutines, setActiveRoutines] = useState({});
    const [pastRoutines, setPastRoutines] = useState({});
    const [activeTab, setActiveTab] = useState('active'); // 'active' or 'challenges'
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(() => !homeCache.has('home_routines_data'));
    const [error, setError] = useState(null);
    const location = useLocation();
    const [expandedRoutineId, setExpandedRoutineId] = useState(location.state?.expandRoutineId || null);
    const [activeCategory, setActiveCategory] = useState(null);
    const [earnedBadges, setEarnedBadges] = useState(new Set());
    const [isAcademyLinked, setIsAcademyLinked] = useState(false);
    const [showChallengeBadge, setShowChallengeBadge] = useState(() => localStorage.getItem('dismissedChallengeBadge') !== 'true');
    const { isOffline } = useContext(OfflineModeContext);

    // Initialize from homeCache if available
    useEffect(() => {
        const cached = homeCache.get('home_routines_data');
        if (cached) {
            if (cached.groupedActive) setActiveRoutines(cached.groupedActive);
            if (cached.groupedPast) setPastRoutines(cached.groupedPast);
            if (cached.logsData) setLogs(cached.logsData);
            if (cached.badgeSetArray) setEarnedBadges(new Set(cached.badgeSetArray));
            if (typeof cached.isAcademyLinked === 'boolean') setIsAcademyLinked(cached.isAcademyLinked);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isAcademyLinked && activeTab === 'active') {
            setActiveTab('training_plan');
        }
    }, [isAcademyLinked, activeTab]);

    const handleLogout = useCallback(() => {
        if (isOffline) {
            alert("Logging out is disabled while in offline mode.");
            return;
        }
        if (window.confirm("Are you sure you want to log out?")) {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            navigate('/login');
        }
    }, [isOffline, navigate]);

    const fetchData = useCallback(async (signal) => {
        if (!homeCache.has('home_routines_data')) {
            setLoading(true);
        }
        setError(null);
        try {
            let activeRoutinesData, pastRoutinesData, logsData, badgesData;
            let usedCache = false;

            // Tier 1: Try Live API
            try {
                const reqOpts = signal ? { signal } : {};
                const [activeRes, pastRes, logsRes, badgesRes, settingsRes] = await Promise.all([
                    api.get('/api/solosync2/v2/routines/', reqOpts),
                    api.get('/api/solosync2/v2/routines/', { ...reqOpts, params: { archived: 'true' } }),
                    api.get('/api/solosync2/logs/', reqOpts).catch(() => ({ data: [] })),
                    api.get('/api/solosync2/challenges/badges/', reqOpts).catch(() => ({ data: [] })),
                    api.get('/api/solosync2/user/settings/', reqOpts).catch(() => ({ data: { is_academy_linked: false } }))
                ]);
                if (signal?.aborted) return;
                activeRoutinesData = activeRes.data;
                pastRoutinesData = pastRes.data;
                logsData = logsRes.data;
                badgesData = badgesRes.data;
                if (settingsRes && settingsRes.data) {
                    setIsAcademyLinked(settingsRes.data.is_academy_linked || false);
                }

                // Call cacheIndividualRoutines for offline SessionPlayer use
                cacheIndividualRoutines([...activeRoutinesData, ...pastRoutinesData]);
            } catch (apiErr) {
                if (signal?.aborted || apiErr?.name === 'CanceledError' || apiErr?.code === 'ERR_CANCELED') {
                    return;
                }
                console.warn('Live API fetch failed, falling back to cache.', apiErr);

                // Tier 2: Bulk Sync Cache (solosync2_master_data)
                let bulkData = null;
                try {
                    const rawBulk = await idbGet('solosync2_master_data');
                    bulkData = typeof rawBulk === 'string' ? JSON.parse(rawBulk) : rawBulk;
                } catch (e) {
                    console.warn('Failed to read bulk sync cache, clearing corrupted data', e);
                    await idbDel('solosync2_master_data');
                    bulkData = null;
                }

                if (bulkData && (bulkData.active_routines || bulkData.past_routines)) {
                    activeRoutinesData = bulkData.active_routines || [];
                    pastRoutinesData = bulkData.past_routines || [];
                    logsData = bulkData.logs || [];
                    badgesData = bulkData.earned_badges || [];
                    usedCache = true;
                } else {
                    // Tier 3: Individual Request Cache (cached_routines_list)
                    let cachedActive = null;
                    let cachedPast = null;
                    try {
                        const rawActive = await idbGet('cached_routines_list');
                        cachedActive = typeof rawActive === 'string' ? JSON.parse(rawActive) : rawActive;
                        
                        const rawPast = await idbGet('cached_past_routines_list');
                        cachedPast = typeof rawPast === 'string' ? JSON.parse(rawPast) : rawPast;
                    } catch (e) {
                        console.warn('Failed to read individual cache, clearing corrupted data', e);
                        await idbDel('cached_routines_list');
                        await idbDel('cached_past_routines_list');
                        cachedActive = null;
                        cachedPast = null;
                    }

                    if (cachedActive || cachedPast) {
                        activeRoutinesData = cachedActive || [];
                        pastRoutinesData = cachedPast || [];
                        logsData = [];
                        badgesData = [];
                        usedCache = true;
                    } else {
                        throw new Error('No cached data available.');
                    }
                }
            }

            if (signal?.aborted) return;

            if (usedCache) {
                window.dispatchEvent(new Event('offlineModeTriggered'));
            }

            setLogs(logsData);
            
            let badgeArray = [];
            if (badgesData) {
                badgeArray = badgesData.map(b => b.routine);
                setEarnedBadges(new Set(badgeArray));
            }
            
            const processAndGroupRoutines = (routinesList) => {
                if (!Array.isArray(routinesList)) routinesList = [];
                const processed = routinesList.map(routine => {
                    const allDrills = routine.drills || [];
                    
                    let uniqueCount = allDrills.length;
                    for (let i = 1; i < allDrills.length; i++) {
                        if (allDrills[i].order <= allDrills[i - 1].order) {
                            uniqueCount = i;
                            break;
                        }
                    }
                    
                    const uniqueDrills = allDrills.slice(0, uniqueCount).sort((a, b) => a.order - b.order);
                    const setCount = uniqueCount > 0 ? Math.floor(allDrills.length / uniqueCount) : 0;
                    
                    let restBetweenSets = 0;
                    if (setCount > 1 && allDrills[uniqueCount - 1]) {
                        restBetweenSets = allDrills[uniqueCount - 1].rest_duration_seconds || 0;
                    }

                    const totalDurationSeconds = allDrills.reduce((total, drillItem) => {
                        const duration = drillItem.duration_seconds || drillItem.drill?.duration_seconds || 0;
                        const rest = drillItem.rest_duration_seconds || drillItem.drill?.rest_duration_seconds || 0;
                        return total + duration + rest;
                    }, 0);

                    return {
                        ...routine,
                        is_challenge: routine.is_challenge || false,
                        challenge_end_date: routine.challenge_end_date || null,
                        challenge_rules: routine.challenge_rules || null,
                        challenge_type: routine.challenge_type || null,
                        required_equipment: routine.required_equipment || [],
                        drills: allDrills, 
                        uniqueDrills,
                        setCount,
                        restBetweenSets,
                        totalDurationSeconds
                    };
                });
                
                return processed.reduce((acc, routine) => {
                    const category = routine.category_name || 'General';
                    if (!acc[category]) {
                        acc[category] = [];
                    }
                    acc[category].push(routine);
                    return acc;
                }, {});
            };

            const hasRunning = Array.isArray(activeRoutinesData) && activeRoutinesData.some(r => r.name && r.name.toLowerCase() === 'running');
            if (Array.isArray(activeRoutinesData) && !hasRunning) {
                activeRoutinesData.push({
                    id: 'running-default',
                    name: 'Running',
                    description: 'Track your outdoor or treadmill runs to build physical conditioning.',
                    category_name: 'Fitness',
                    is_challenge: false,
                    assignment_context: 'Fitness',
                    drills: [],
                    uniqueDrills: [],
                    setCount: 0,
                    restBetweenSets: 0,
                    totalDurationSeconds: 0,
                    required_equipment: []
                });
            }

            const groupedActive = processAndGroupRoutines(activeRoutinesData);
            const groupedPast = processAndGroupRoutines(pastRoutinesData);

            setActiveRoutines(groupedActive);
            setPastRoutines(groupedPast);

            // Update in-memory SWR homeCache
            homeCache.set('home_routines_data', {
                groupedActive,
                groupedPast,
                logsData,
                badgeSetArray: badgeArray,
                isAcademyLinked: isAcademyLinked
            });

            // Auto-expand category if location state requested a specific routine
            const expandId = location.state?.expandRoutineId;
            if (expandId) {
                let targetCategory = null;
                let foundInPast = false;
                let targetRoutine = null;

                activeRoutinesData.forEach(r => {
                    if (r.id === expandId) targetRoutine = r;
                });

                if (!targetRoutine) {
                    pastRoutinesData.forEach(r => {
                        if (r.id === expandId) {
                            targetRoutine = r;
                            foundInPast = true;
                        }
                    });
                }

                if (targetRoutine) {
                    if (targetRoutine.is_challenge) {
                        const isPastChallenge = targetRoutine.challenge_end_date && new Date(targetRoutine.challenge_end_date) < new Date();
                        targetCategory = isPastChallenge ? 'Past Challenges' : 'Active Challenges';
                        setActiveTab('challenges');
                    } else {
                        targetCategory = targetRoutine.category_name || 'General';
                        if (foundInPast) setActiveTab('past');
                    }
                    setActiveCategory(targetCategory);
                }
            }
        } catch (err) {
            if (signal?.aborted) return;
            setError(isOffline ? 'No cached data available. Please connect to the internet.' : 'Failed to fetch routines.');
            console.error(err);
        } finally {
            if (!signal?.aborted) {
                setLoading(false);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state?.expandRoutineId, isOffline]);

    useEffect(() => {
        const controller = new AbortController();
        fetchData(controller.signal);
        return () => {
            controller.abort();
        };
    }, [fetchData]);

    const toggleRoutineDetails = useCallback((routineId) => {
        setExpandedRoutineId(prev => prev === routineId ? null : routineId);
    }, []);

    const filterRoutines = useCallback((routinesDict, condition) => {
        return Object.fromEntries(
            Object.entries(routinesDict)
                .map(([cat, arr]) => [cat, arr.filter(condition)])
                .filter(([cat, arr]) => arr.length > 0)
        );
    }, []);

    const trainingPlanRoutines = useMemo(() => filterRoutines(activeRoutines, r => !r.is_challenge && ((r.assignment_context && r.assignment_context !== 'General') || r.name?.toLowerCase() === 'running')), [activeRoutines, filterRoutines]);
    const exploreRoutines = useMemo(() => filterRoutines(activeRoutines, r => !r.is_challenge && (!r.assignment_context || r.assignment_context === 'General')), [activeRoutines, filterRoutines]);
    const activeNonChallengeRoutines = useMemo(() => filterRoutines(activeRoutines, r => !r.is_challenge), [activeRoutines, filterRoutines]);

    const allChallenges = useMemo(() => [
        ...Object.values(activeRoutines).flat().filter(r => r.is_challenge),
        ...Object.values(pastRoutines).flat().filter(r => r.is_challenge)
    ], [activeRoutines, pastRoutines]);

    const uniqueChallenges = useMemo(() => {
        const uniqueMap = new Map();
        allChallenges.forEach(r => uniqueMap.set(r.id, r));
        return Array.from(uniqueMap.values());
    }, [allChallenges]);

    const activeChallengesList = useMemo(() => uniqueChallenges.filter(r => {
        if (!r.challenge_end_date) return true;
        return new Date(r.challenge_end_date) >= new Date();
    }), [uniqueChallenges]);

    const pastChallengesList = useMemo(() => uniqueChallenges.filter(r => {
        if (!r.challenge_end_date) return false;
        return new Date(r.challenge_end_date) < new Date();
    }), [uniqueChallenges]);

    const challengesRoutines = useMemo(() => {
        const res = {};
        if (activeChallengesList.length > 0) {
            res['Active Challenges'] = activeChallengesList;
        }
        if (pastChallengesList.length > 0) {
            res['Past Challenges'] = pastChallengesList;
        }
        return res;
    }, [activeChallengesList, pastChallengesList]);

    const currentRoutines = useMemo(() => {
        if (activeTab === 'training_plan') return trainingPlanRoutines;
        if (activeTab === 'explore') return exploreRoutines;
        if (activeTab === 'challenges') return challengesRoutines;
        return activeNonChallengeRoutines;
    }, [activeTab, trainingPlanRoutines, exploreRoutines, challengesRoutines, activeNonChallengeRoutines]);

    const activeChallengesCount = useMemo(() => Object.values(activeRoutines).flat().filter(r => r.is_challenge).length, [activeRoutines]);




    if (loading) {
        return (
            <div className="max-w-4xl mx-auto px-4 mt-8">
                <div className="animate-pulse space-y-4">
                    <div className="h-10 bg-gray-800/60 rounded w-1/4 mb-8"></div>
                    {[1, 2, 3].map((n) => (
                        <div key={n} className="bg-gray-800/40 backdrop-blur-sm border border-white/10 rounded-xl p-6 shadow-lg">
                            <div className="flex justify-between items-center mb-4">
                                <div className="h-6 bg-gray-700/50 rounded w-1/3"></div>
                                <div className="h-10 bg-gray-700/50 rounded w-24"></div>
                            </div>
                            <div className="flex gap-2 mb-4">
                                <div className="h-5 bg-gray-700/50 rounded-full w-20"></div>
                                <div className="h-5 bg-gray-700/50 rounded-full w-20"></div>
                            </div>
                            <div className="h-4 bg-gray-700/50 rounded w-1/4"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center mt-12 bg-gray-800 p-8 rounded-lg shadow-lg max-w-md mx-auto relative overflow-hidden border border-gray-700">
                <div className="absolute top-0 left-0 w-full h-1 bg-red-500"></div>
                
                <svg className="w-16 h-16 text-teal-400 mb-4 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>

                <h3 className="text-xl font-bold text-teal-300 mb-2">Connection Issue</h3>
                <p className="text-gray-400 text-center mb-6">
                    {error}
                </p>

                <div className="flex flex-col sm:flex-row items-center gap-4">
                    <button 
                        onClick={fetchData}
                        className="flex items-center space-x-2 bg-gradient-to-r from-teal-500 to-teal-400 hover:from-teal-400 hover:to-teal-300 text-gray-900 font-bold py-2 px-6 rounded-full transition-all duration-300 shadow-[0_0_15px_rgba(20,184,166,0.3)] hover:shadow-[0_0_25px_rgba(20,184,166,0.5)] active:scale-95"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        <span>Try Again</span>
                    </button>
                    
                    <button 
                        onClick={handleLogout}
                        disabled={isOffline}
                        className={`flex items-center space-x-2 border font-bold py-2 px-6 rounded-full transition-all duration-300 shadow-md active:scale-95 ${
                            isOffline
                                ? 'bg-gray-800 text-gray-500 border-gray-700 cursor-not-allowed'
                                : 'bg-gray-800 hover:bg-red-500/20 text-gray-300 hover:text-red-400 border-gray-600 hover:border-red-500/50'
                        }`}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        <span>Log Out</span>
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 py-6 md:py-8">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-primary to-teal-200 tracking-tight">Routines</h1>
            </div>

            {showChallengeBadge && activeChallengesCount > 0 && (
                <div className="mb-6 animate-fade-in relative">
                    <div 
                        className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-5 py-3 rounded-xl shadow-lg shadow-orange-500/20 flex items-center justify-between border border-orange-400/50 cursor-pointer hover:scale-[1.02] transition-transform active:scale-95"
                        onClick={() => {
                            setActiveTab(isAcademyLinked ? 'explore' : 'active');
                            setActiveCategory(null);
                            setExpandedRoutineId(null);
                        }}
                    >
                        <div className="flex items-center gap-3">
                            <Trophy className="w-5 h-5 text-yellow-300" />
                            <span className="font-extrabold tracking-wide">
                                {activeChallengesCount} Active Challenge{activeChallengesCount !== 1 ? 's' : ''} available!
                            </span>
                        </div>
                        <button 
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowChallengeBadge(false);
                                localStorage.setItem('dismissedChallengeBadge', 'true');
                            }}
                            className="text-white/80 hover:text-white hover:bg-white/20 rounded-full p-1.5 transition-colors"
                            aria-label="Dismiss"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
                        </button>
                    </div>
                </div>
            )}

            <StatsBanner logs={logs} compact={true} />

            <div className="flex space-x-1 bg-gray-800/60 p-1 rounded-xl mb-8 mt-6">
                {isAcademyLinked ? (
                    <>
                        <button
                            onClick={() => { setActiveTab('training_plan'); setActiveCategory(null); setExpandedRoutineId(null); }}
                            className={`flex-1 py-2.5 px-4 text-sm font-bold rounded-lg transition-all duration-300 ${activeTab === 'training_plan' ? 'bg-gradient-to-r from-teal-500 to-teal-400 text-gray-900 shadow-lg' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'}`}
                        >
                            Your Training Plan
                        </button>
                        <button
                            onClick={() => { setActiveTab('explore'); setActiveCategory(null); setExpandedRoutineId(null); }}
                            className={`flex-1 py-2.5 px-4 text-sm font-bold rounded-lg transition-all duration-300 ${activeTab === 'explore' ? 'bg-gradient-to-r from-teal-500 to-teal-400 text-gray-900 shadow-lg' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'}`}
                        >
                            Explore
                        </button>
                        <button
                            onClick={() => { setActiveTab('challenges'); setActiveCategory(null); setExpandedRoutineId(null); }}
                            className={`flex-1 py-2.5 px-4 text-sm font-bold rounded-lg transition-all duration-300 ${activeTab === 'challenges' ? 'bg-gradient-to-r from-teal-500 to-teal-400 text-gray-900 shadow-lg' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'}`}
                        >
                            Challenges
                        </button>
                    </>
                ) : (
                    <>
                        <button
                            onClick={() => { setActiveTab('active'); setActiveCategory(null); setExpandedRoutineId(null); }}
                            className={`flex-1 py-2.5 px-4 text-sm font-bold rounded-lg transition-all duration-300 ${activeTab === 'active' ? 'bg-gradient-to-r from-teal-500 to-teal-400 text-gray-900 shadow-lg' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'}`}
                        >
                            Active Routines
                        </button>
                        <button
                            onClick={() => { setActiveTab('challenges'); setActiveCategory(null); setExpandedRoutineId(null); }}
                            className={`flex-1 py-2.5 px-4 text-sm font-bold rounded-lg transition-all duration-300 ${activeTab === 'challenges' ? 'bg-gradient-to-r from-teal-500 to-teal-400 text-gray-900 shadow-lg' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'}`}
                        >
                            Challenges
                        </button>
                    </>
                )}
            </div>

            {Object.keys(currentRoutines).length === 0 ? (
                <div className="flex flex-col items-center justify-center mt-12 p-8 bg-gray-800/20 backdrop-blur-sm border border-white/5 rounded-2xl shadow-xl">
                    <svg className="w-24 h-24 text-gray-600 mb-6 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                    <h3 className="text-2xl font-semibold text-gray-300 mb-2">No Routines Found</h3>
                    <p className="text-gray-500 text-center max-w-md">
                        There are no routines available right now. Please add some from your admin dashboard to get started.
                    </p>
                </div>
            ) : (
                <div className="space-y-6">
                    {activeCategory === null ? (
                        <div className="flex flex-col gap-4">
                            {Object.entries(currentRoutines).map(([category, categoryRoutines]) => (
                                <div
                                    key={category}
                                    onClick={() => setActiveCategory(category)}
                                    className={`relative min-h-[140px] rounded-2xl p-6 cursor-pointer overflow-hidden shadow-lg transition-transform hover:scale-[1.01] active:scale-[0.98] bg-slate-800/80 backdrop-blur-md border border-white/5 ${getCategoryStyle(category)}`}
                                >
                                    <div className="relative z-10 flex flex-col justify-end h-full">
                                        <h2 className="text-2xl font-bold tracking-tight text-white/90">
                                            {category}
                                        </h2>
                                        <p className="text-white/80 font-medium mt-1">
                                            {categoryRoutines.length} routine{categoryRoutines.length !== 1 ? 's' : ''}
                                        </p>
                                    </div>
                                    {getCategoryWatermark(category)}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="animate-fade-in transition-opacity duration-300">
                            <div 
                                onClick={() => setActiveCategory(null)}
                                className={`relative min-h-[120px] rounded-2xl p-6 mb-8 cursor-pointer overflow-hidden shadow-lg transition-transform hover:scale-[1.01] active:scale-[0.98] bg-slate-800/80 backdrop-blur-md border border-white/5 ${getCategoryStyle(activeCategory)}`}
                            >
                                <div className="relative z-10 flex flex-col justify-end h-full">
                                    <h2 className="text-2xl font-bold tracking-tight text-white/90">
                                        {activeCategory}
                                    </h2>
                                    <p className="text-white/80 font-medium mt-1">
                                        {currentRoutines[activeCategory]?.length || 0} routine{(currentRoutines[activeCategory]?.length || 0) !== 1 ? 's' : ''}
                                    </p>
                                </div>
                                {getCategoryWatermark(activeCategory)}
                            </div>

                            <div className="space-y-4">
                                {((currentRoutines[activeCategory] || []).sort((a, b) => {
                                    if (a.name.toLowerCase() === 'running') return -1;
                                    if (b.name.toLowerCase() === 'running') return 1;
                                    return 0;
                                })).map((routine) => (
                                    <div key={routine.id} className="bg-gray-800/40 backdrop-blur-sm border border-white/10 rounded-xl shadow-lg hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:shadow-primary/10 hover:-translate-y-1 transition-all duration-300 overflow-hidden group">
                                        <div className="p-5 flex justify-between items-center cursor-pointer" onClick={() => toggleRoutineDetails(routine.id)}>
                                            <div className="flex-grow flex-1 min-w-0 pr-4">
                                                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-2">
                                                    <h2 className="text-2xl font-bold text-gray-100 group-hover:text-primary transition-colors">{routine.name}</h2>

                                                    {routine.is_challenge && (
                                                        <span className="bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold px-3 py-1 rounded-full text-xs tracking-wider uppercase shadow-lg border border-orange-400/50 flex-shrink-0">
                                                            Challenge
                                                        </span>
                                                    )}
                                                    {routine.is_challenge && earnedBadges.has(routine.id) && (
                                                        <span className="flex items-center gap-1.5 bg-gradient-to-r from-yellow-400 to-yellow-600 text-gray-900 font-bold px-3 py-1 rounded-full text-xs tracking-wider uppercase shadow-[0_0_10px_rgba(250,204,21,0.3)] border border-yellow-300 flex-shrink-0">
                                                            <Trophy className="w-4 h-4" />
                                                            Badge Awarded
                                                        </span>
                                                    )}
                                                    {routine.setCount > 1 && (
                                                        <div className="flex items-center gap-2 text-xs font-semibold">
                                                            <span className="bg-primary/20 text-primary border border-primary/30 px-2.5 py-1 rounded-full inline-flex items-center whitespace-nowrap">
                                                                {routine.setCount} Sets
                                                            </span>
                                                            <span className="bg-secondary/20 text-secondary border border-secondary/30 px-2.5 py-1 rounded-full inline-flex items-center whitespace-nowrap">
                                                                {routine.restBetweenSets}s Rest
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                {routine.name.toLowerCase() === 'running' ? (
                                                    <p className="text-sm text-gray-400 font-medium">Self-paced <span className="text-gray-600">•</span> Distance & Time tracking</p>
                                                ) : (
                                                    <p className="text-sm text-gray-400 font-medium">{routine.drills.length} total drills <span className="text-gray-600">•</span> Duration: {Math.floor((routine.totalDurationSeconds || 0) / 60)}:{String((routine.totalDurationSeconds || 0) % 60).padStart(2, '0')}</p>
                                                )}
                                                <EquipmentBadges equipment={routine.required_equipment} variant="compact" />
                                            </div>
                                            <div className="flex items-center space-x-3 ml-4">
                                                {routine.is_challenge && routine.challenge_end_date && new Date(routine.challenge_end_date) < new Date() ? (
                                                    <span className="flex items-center justify-center bg-gray-700 text-gray-400 font-bold py-2.5 px-5 rounded-lg opacity-70 cursor-not-allowed border border-gray-600">
                                                        <span>Ended</span>
                                                    </span>
                                                ) : routine.name.toLowerCase() === 'running' ? (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); navigate('/running-dashboard', { state: { routineId: routine.id } }); }}
                                                        className="flex items-center justify-center bg-primary hover:bg-teal-400 text-gray-900 font-bold py-2.5 px-5 rounded-lg transition-transform hover:scale-105 active:scale-95 shadow-lg shadow-primary/25"
                                                    >
                                                        <svg className="w-5 h-5 mr-1.5" fill="currentColor" viewBox="0 0 20 20"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" /></svg>
                                                        <span>Log Run</span>
                                                    </button>
                                                ) : (
                                                    <Link
                                                        to={`/session/${routine.id}`}
                                                        className="flex items-center justify-center bg-primary hover:bg-teal-400 text-gray-900 font-bold py-2.5 px-5 rounded-lg transition-transform hover:scale-105 active:scale-95 shadow-lg shadow-primary/25"
                                                        onClick={(e) => e.stopPropagation()} 
                                                    >
                                                        <svg className="w-5 h-5 mr-1.5" fill="currentColor" viewBox="0 0 20 20"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" /></svg>
                                                        <span>Start</span>
                                                    </Link>
                                                )}
                                                <span className={`transform transition-transform duration-300 text-gray-400 group-hover:text-gray-200 ${expandedRoutineId === routine.id ? 'rotate-180' : ''}`}>
                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                                                </span>
                                            </div>
                                        </div>
                                        {expandedRoutineId === routine.id && (
                                            <div className="p-4 border-t border-gray-700">
                                                <p className="text-gray-300 mb-4">{routine.description}</p>
                                                
                                                {routine.is_challenge && routine.challenge_rules && (
                                                    <div className="mb-6 p-4 bg-orange-500/10 border-l-4 border-orange-500 rounded-r-lg">
                                                        <h3 className="text-orange-400 font-bold mb-2 flex items-center">
                                                            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                            </svg>
                                                            Challenge Rules
                                                        </h3>
                                                        <div className="text-gray-300 text-sm whitespace-pre-wrap">{routine.challenge_rules}</div>
                                                    </div>
                                                )}

                                                {routine.is_challenge && !isOffline && (
                                                    <div className="mb-6">
                                                        <ChallengeLeaderboard routineId={routine.id} challengeType={routine.challenge_type} challengeEndDate={routine.challenge_end_date} />
                                                    </div>
                                                )}
                                                
                                                {routine.setCount > 1 && (
                                                    <div className="mb-4">
                                                        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-600 pb-2">
                                                            Circuit Details (Set 1 of {routine.setCount})
                                                        </h3>
                                                    </div>
                                                )}

                                                {routine.name.toLowerCase() === 'running' ? (
                                                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-6 text-center shadow-inner mt-4">
                                                        <Activity className="w-12 h-12 text-orange-400 mx-auto mb-3" />
                                                        <h4 className="text-lg font-bold text-gray-200 mb-2 tracking-wide">Track Your Runs</h4>
                                                        <p className="text-gray-400 text-sm max-w-md mx-auto">
                                                            Log your outdoor or treadmill runs to build your physical conditioning. These runs are automatically excluded from the weekly leaderboard score, but they will maintain your active streak!
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                        {routine.uniqueDrills?.map((drill, idx) => (
                                                            <DrillCard key={drill.drill?.id || idx} drill={drill} />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default RoutineListPage;